<script>
	import Numbers from './Numbers.svelte';

	const SET_SUDOKU = '1,2,3,4,5,6,7,8,9';
	const SET_SANDWICH = '2,3,4,5,6,7,8';

	let setString = SET_SUDOKU;
	$: set = [...new Set(setString.split(',').map(o => parseInt(o.trim())).filter(o => !!o && o > 0))];

	let expectedSum = 12;

	const sum = arr => arr.reduce((a, b) => a + b, 0);

	$: combinations = (() => {
		const result = [];
		const f = function(prefix, chars) {
			for (let i = 0; i < chars.length; i++) {
				result.push([...prefix, chars[i]]);
				f([...prefix, chars[i]], chars.slice(i + 1));
			}
		}
		f([], set);
		return result;
	})();

	$: options = combinations.filter(option => {
		return sum(option) === expectedSum;
	}).sort((a, b) => {
		return a.length - b.length;
	});

	$: optionsNotMuted = options.filter((v, i) => !muted[i]);

	$: lengths = [...new Set(options.map(option => option.length))];
	$: lengthsNotMuted = [...new Set(optionsNotMuted.map(option => option.length))];

	$: used = [...new Set(options.reduce((a, b) => [...a, ...b], []))];
	$: usedNotMuted = [...new Set(optionsNotMuted.reduce((a, b) => [...a, ...b], []))];
	$: unused = set.filter(x => !new Set(usedNotMuted).has(x));
	$: alwaysUsed = set.filter(x => optionsNotMuted.filter(options => options.includes(x)).length === optionsNotMuted.length);

	$: max = sum(set);

	$: muted = new Array(options.length);
</script>

<main class="container pt-3 pt-md-4 d-flex flex-column">
	<h1>
		<img src="./logo.png" alt="∑"/>
		SumUp
	</h1>
	<p>
		SumUp is a simple tool that shows all possible combinations of numbers that add up to a given sum.
		It's useful for certain types of puzzles, like for example
		<a href="https://www.youtube.com/watch?v=hOAB65KFl6I" target="_blank" rel="noopener">🔗 this killer sudoku</a>.
	</p>

	<div class="bg-light border rounded p-4">
		<div class="row">
			<div class="col-12 col-md-6">
				<div class="form-group">
					<label for="set">Out of those available numbers:</label>
					<input id="set" type="text" bind:value={setString} class="form-control text-center">
				</div>

				<div class="my-3 small">
					<label>Presets:</label>
					<button class="btn btn-{setString === SET_SUDOKU ? '' : 'outline-'}primary btn-sm" on:click={_ => setString = SET_SUDOKU}>
						Sudoku
					</button>
					<button class="btn btn-{setString === SET_SANDWICH ? '' : 'outline-'}primary btn-sm" on:click={_ => setString = SET_SANDWICH}>
						Sandwich Sudoku
					</button>
				</div>

				<p>
					<Numbers v={set}/>
					<span class="text-nowrap">(sum: <Numbers v={[max]}/>)</span>
				</p>
			</div>
			<div class="col-12 col-md-6">
				<label for="expectedSum">Find combinations that sum up to:</label>
				<div class="input-group mb-3">
					<div class="input-group-prepend">
						<button class="btn btn-outline-secondary" type="button" on:click={_ => expectedSum = Math.max(0, expectedSum - 1)}>−</button>
					</div>
					<input id="expectedSum" type="number" bind:value={expectedSum} class="form-control text-center" min="0" max={max}>
					<div class="input-group-append">
						<button class="btn btn-outline-secondary" type="button" on:click={_ => expectedSum = Math.min(expectedSum + 1, max)}>+</button>
					</div>
				</div>
			</div>
		</div>
	</div>

	<div class="row my-3 flex-grow-1">
		<div class="col-6">
			<label>Possible combinations: ({options.length})</label>
			<ul class="list-unstyled">
				{#each options as option, i}
					<li class="my-2 cursor-pointer" on:click={_ => muted[i] = !muted[i]}>
						<Numbers v={option} muted={n => muted[i]}/>
					</li>
				{:else}
					<li class="my-2">
						<Numbers v={[]}/>
					</li>
				{/each}
			</ul>
		</div>
		<div class="col-6">
			<ul class="list-unstyled">
				<li class="my-2">
					<label>Lengths:</label>
					<br/>
					<Numbers v={lengths} muted={n => !lengthsNotMuted.includes(n)} click={(len, toMuted) => {
						options.forEach((option, i) => {
							if (option.length === len) {
								muted[i] = toMuted;
							}
						});
					}}/>
				</li>
				<li class="my-2">
					<label>Used numbers:</label>
					<br/>
					<Numbers v={used} muted={n => !usedNotMuted.includes(n)} click={(n, toMuted) => {
						options.forEach((option, i) => {
							if (option.includes(n)) {
								muted[i] = toMuted;
							}
						});
					}}/>
				</li>
				<hr/>
				<li class="my-2">
					<label>Unused numbers:</label>
					<br/>
					<Numbers v={unused}/>
				</li>
				<li class="my-2">
					<label>Always used:</label>
					<br/>
					<Numbers v={alwaysUsed}/>
				</li>
			</ul>
		</div>
	</div>
	<footer class="border-top mt-5 small d-flex flex-column flex-md-row justify-content-between">
		<p class="my-3">
			Made with ❤ by <a href="https://avris.it" target="_blank" rel="noopener">🧑‍💻 Andrea Vos</a>
			using <a href="https://svelte.dev/" target="_blank" rel="noopener">🔗 Svelte</a>.
		</p>
		<ul class="list-inline my-3">
			<li class="list-inline-item">
				<a href="https://gitlab.com/Avris/SumUp" target="_blank" rel="noopener">🔗 Source code</a>
			</li>
			<li class="list-inline-item">
				<a href="https://paypal.me/AndreAvris" target="_blank" rel="noopener">🍺 Buy me a beer</a>
			</li>
			<li class="list-inline-item">
				<a href="https://bunq.me/AndreaVos" target="_blank" rel="noopener">/ bank</a>
			</li>
		</ul>
	</footer>
</main>

<style lang="scss" global>
	@import "./bootstrap.scss";

	main {
		min-height: 100vh;
	}

	label {
		font-weight: bold;
	}

	h1 img {
		height: 1.5em;
		width: 1.5em;
	}

	.cursor-pointer {
		cursor: pointer;
	}
</style>
